import React from 'react';
import { TFunction } from 'i18next';
import { IMonitoringToolkit } from '@components-clean/core/monitoring-toolkit/interface';
import { IUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import ControlsObservations from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/controls-observations';
import KRIOverview from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/kri-overview';
import SiteDatasetReview from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/site-dataset-reviews';
import SiteObservations from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/site-observations';
import StudyDatasetReview from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/study-dataset-review';
import StudyObservations from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/study-observations';
import MonitoringToolkitWrapper, {
    MonitoringToolkitWrapperProps,
} from '@pages-clean/central-monitoring/monitoring-toolkit/wrapper';
import LocalStorage from '@utils-clean/local-storage-service';
import { IUser, Permissions } from '@contexts/user-context';
import Constants from '@utils/constants';
import Modules from '@config/modules';

export const centralMonitoringToolkitHelper: IMonitoringToolkit = {
    TOOLKIT_STATE_KEY: 'CentralMonitoringToolkit.OpenState',
    BOTTOM_PANEL_MIN_SIZE: 100,
    COLLAPSED_SIZE: '0fr',
    AUTO_SIZE: '1fr',
    translationKey: 'cm',

    canUserViewToolkit(user: IUser, moduleId: number) {
        return (
            (user?.hasModule?.(Modules.CentralMonitoring2) &&
                user?.can?.({
                    module: moduleId,
                    element: Constants.PageElements.MonitoringToolkit,
                    permission: Permissions.Enabled,
                })) ??
            false
        );
    },

    canEditToolkit(user: IUser) {
        return !!user?.canWrite?.({
            module: Constants.Modules.CM2,
            element: Constants.DataElements.CentralMonitoringDefault,
            permission: Permissions.ReadWrite,
        });
    },

    tabsContent(
        organisationKey: string,
        studyRef: string,
        studyNumber: string,
        canEdit: boolean,
        isHalfScreen: boolean,
        datasetId: number,
        _setSelectedTab,
        t: TFunction,
    ) {
        return [
            {
                title: t('StudyObservations'),
                shortTitle: t('StudyObservationsShort'),
                content: (
                    <StudyObservations
                        orgKey={organisationKey}
                        studyRef={studyRef}
                        studyNumber={studyNumber}
                        canEdit={canEdit}
                        isHalfScreen={isHalfScreen}
                    />
                ),
            },
            {
                title: t('SiteObservations'),
                shortTitle: t('SiteObservationsShort'),
                content: (
                    <SiteObservations
                        orgKey={organisationKey}
                        studyRef={studyRef}
                        studyNumber={studyNumber}
                        canEdit={canEdit}
                        isHalfScreen={isHalfScreen}
                    />
                ),
            },
            {
                title: t('ControlsObservations'),
                shortTitle: t('ControlsObservationsShort'),
                content: (
                    <ControlsObservations
                        orgKey={organisationKey}
                        studyRef={studyRef}
                        studyNumber={studyNumber}
                        canEdit={canEdit}
                        isHalfScreen={isHalfScreen}
                    />
                ),
            },
            {
                title: t('kriOverview.title.full'),
                shortTitle: t('kriOverview.title.short'),
                content: <KRIOverview studyRef={studyRef} datasetId={datasetId} canEdit={canEdit} />,
            },
            {
                title: t('studyDatasetReview.title.full'),
                shortTitle: t('studyDatasetReview.title.short'),
                content: <StudyDatasetReview studyRef={studyRef} datasetId={datasetId} canEdit={canEdit} />,
            },
            {
                title: t('siteDatasetReview.title.full'),
                shortTitle: t('siteDatasetReview.title.short'),
                content: <SiteDatasetReview studyRef={studyRef} datasetId={datasetId} canEdit={canEdit} />,
            },
        ];
    },

    clearMonitoringToolKitOpenState(user: IUser) {
        LocalStorage.removeItem(user, this.TOOLKIT_STATE_KEY);
    },

    hasUnsavedChanges(unsavedChangesContext: IUnsavedChangesContext) {
        return unsavedChangesContext.anyCentralMonitoringToolkitHasChanges();
    },

    clearUnsavedChanges(unsavedChangesContext: IUnsavedChangesContext) {
        unsavedChangesContext.centralMonitoringToolkitDiscardChanges();
    },
};

type Props = Omit<MonitoringToolkitWrapperProps, 'toolkitHelper'>;

export const CentralMonitoringToolkit = (props: Props) => {
    return <MonitoringToolkitWrapper {...props} toolkitHelper={centralMonitoringToolkitHelper} />;
};
